// MyRatesPanel — the agent's own commission rates (read-only).
//
// U0 (2026-07-08) rewrite: the old version died on a dead endpoint
// ('/admin/me-agent/dashboard/my-rates' → deleted controller method → the
// tab showed "Something went wrong" since the hierarchy restructure).
// It now reads the agent's full six-rate matrix from
// GET /admin/me-agent/my-commission-rates (the same source the downline-
// creation ceilings use), and the username comes from useAdminAuth —
// zero extra round-trips, one request total.
//
// UI doctrine (docs/AGENT_UI_SIMPLIFICATION_REPORT.md): one question per
// screen — "what do I earn?" — answered with six plain-word rows, grouped
// Casino / Cricket. Both themes verified; secondary text stays legible in
// dark (no /40 opacities).
//
// Agent-frontend policy: an agent's LEVEL is never surfaced to the agent
// (only admins see levels) — so no level names here, just the rates.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';

type Rates = {
  winLossPct: number;
  profitSharePct: number; commissionSharePct: number;
};

// Plain-word catalogue — label + one sentence a first-time broker understands.
// Values are translation keys; the component passes them through t().
const GROUPS: Array<{
  title: string; icon: string;
  rows: Array<{ key: keyof Rates; label: string; plain: string }>;
}> = [
  {
    title: 'agent.games.section.casino', icon: 'casino',
    rows: [
      { key: 'winLossPct',   label: 'agent.myRates.row.winLoss.label',  plain: 'agent.myRates.row.winLoss.plain' },
    ],
  },
  {
    title: 'agent.games.cricket.name', icon: 'sports_cricket',
    rows: [
      { key: 'profitSharePct',     label: 'agent.myRates.row.profitShare.label', plain: 'agent.myRates.row.profitShare.plain' },
      { key: 'commissionSharePct', label: 'agent.myRates.row.commShare.label',   plain: 'agent.myRates.row.commShare.plain' },
    ],
  },
];

export default function MyRatesPanel() {
  const t = useT();
  const { admin } = useAdminAuth();
  const [rates, setRates] = useState<Rates | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<Rates>('/admin/me-agent/my-commission-rates');
        if (!cancelled) setRates(r);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || t('agent.myRates.error'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
        {err}
      </div>
    );
  }
  if (!rates) {
    return (
      <div className="grid place-items-center py-12 text-arena-600 dark:text-neon-200/70 text-sm">
        <Icon name="progress_activity" size={24} className="animate-spin" />
        <p className="mt-2">{t('agent.myRates.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Identity hero — saturated gradient + white text works in BOTH themes. */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-800 text-white p-4 sm:p-5 ring-1 ring-inset ring-emerald-300/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/15 grid place-items-center shrink-0 backdrop-blur-sm">
            <Icon name="percent" size={22} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-[18px] sm:text-[22px] leading-tight truncate">@{admin?.username}</div>
            <div className="text-[12px] sm:text-[12.5px] text-white/90 mt-0.5">
              {t('agent.myRates.hero.subtitle')}
            </div>
          </div>
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="rounded-2xl bg-white dark:bg-white/5 ring-1 ring-inset ring-arena-200/60 dark:ring-white/10 overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-4 pb-2 text-[12px] font-extrabold uppercase tracking-wider text-arena-700 dark:text-neon-200/85">
            <Icon name={g.icon} size={16} className="text-emerald-600 dark:text-emerald-300" />
            {t(g.title)}
          </div>
          <div className="divide-y divide-arena-100 dark:divide-white/8">
            {g.rows.map((row) => {
              const v = Number(rates[row.key] ?? 0);
              const off = v <= 0;
              return (
                <div key={row.key} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-bold text-arena-900 dark:text-white">{t(row.label)}</div>
                    <p className="text-[12px] leading-snug text-arena-600 dark:text-neon-200/75 mt-0.5">{t(row.plain)}</p>
                  </div>
                  <div className={`shrink-0 text-right font-black text-[22px] tabular-nums ${
                    off ? 'text-arena-400 dark:text-neon-200/60' : 'text-emerald-700 dark:text-emerald-300'
                  }`}>
                    {v}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <p className="text-[12px] text-arena-600 dark:text-neon-200/70 px-1">
        {t('agent.myRates.footer.zero')}
      </p>
    </div>
  );
}
